import type { TurnEvent } from '@stores/assistantTurn'

import { GetAccessToken, refreshAccessTokenShared } from '../baseService'

/*
 * SSE client for POST /api/app/assistant/conversations/{id}/messages.
 * EventSource cannot send an Authorization header or a POST body, so this uses
 * fetch + ReadableStream and parses the text/event-stream frames manually.
 */

export interface AssistantStreamHandlers {
  signal?: AbortSignal
  /** Every content/progress event of the turn, already typed for turnReducer. */
  onEvent: (_event: TurnEvent) => void
  onDone: (_payload: { messageId: string }) => void
  onError: (_code: string, _message?: string) => void
  /**
   * The conversation was given its model-written title. Sent after `done` (or not at all): the
   * turn is over by then, so it changes nothing about the turn.
   */
  onTitle?: (_payload: { conversationId: string; title: string }) => void
  /**
   * What the server made of the page context sent with the question: the record it uses, under
   * the record's own number or name, or `used: false` when it does not use it. Sent before
   * anything of the turn, and only when the question carried a context.
   */
  onContext?: (_payload: AssistantContextEvent) => void
}

export interface AssistantContextEvent {
  used: boolean
  recordType?: string
  recordId?: string
  /** A list screen instead of a record: the kind of list and, for a case list, its case type. */
  listKind?: string
  serviceType?: string
  label?: string
}

export interface AssistantCitation {
  type: string
  id: string
  label: string
  /**
   * false: the user may not open this record's screen. Its name in the answer is shown in bold,
   * not as a link, and it is never a pill.
   */
  openable?: boolean
  /** appointment_session only: the session start (UTC) its label was made from. */
  start?: string
  /**
   * appointment_session only: the session's zone (IANA) — from the server, then the zone the
   * answer shows the start in.
   */
  timeZone?: string
}

export interface AssistantClarificationOption {
  value: string
  label: string
  description?: string
}

export interface AssistantClarificationEvent {
  messageId: string
  question: string
  kind: 'choice' | 'text'
  options?: AssistantClarificationOption[]
  allowFreeText: boolean
}

/** A structured piece of an assistant message (server-built). */
export interface AssistantPart {
  type: string
  label?: string
  route?: string
  query?: Record<string, string>
  /** confirmation: the proposed action the buttons act on */
  actionId?: string
  /** confirmation: what will happen (may contain date tokens) */
  lines?: string[]
  /** confirmation: proposed | executing | executed | cancelled | expired | failed */
  status?: string
  expiresAt?: string
  confirmLabel?: string
  /** guide: the user-guide page the link opens */
  docKey?: string
  /** confirmation of an email action: the email the user is about to save */
  email?: AssistantEmailPreview
  /** notice: what the server's remark is about (UNVERIFIED_REFERENCE, ANSWER_CUT_SHORT); its text is `label` */
  code?: string
  /** session_table: the calendar sessions a search listed, one row each, in order */
  sessions?: AssistantSessionRow[]
  /** appointment_history: each version of a case's appointment, newest first */
  appointments?: AssistantAppointmentHistoryRow[]
}

/** One row of an appointment history table, as the case screen's Appointment History shows it. */
export interface AssistantAppointmentHistoryRow {
  /** The appointment's start and end (UTC). */
  start?: string
  end?: string
  /** The appointment's own zone (IANA). */
  timeZone?: string
  specialist?: string
  centre?: string
  /** Appointment Accepts: In-person, Telehealth, Phone… */
  heldAs?: string
  /** Requested, Confirmed or Cancelled */
  status?: string
  cancellationReason?: string
  updatedBy?: string
  /** Updated Time (UTC). */
  updatedAt?: string
}

/** One row of a session table: a session and its specialist and centre, by the server's ids. */
export interface AssistantSessionRow {
  sessionId: string
  /** The session's start (UTC). */
  start?: string
  /** The session's own zone (IANA); none = the organisation's. */
  timeZone?: string
  /** openable false: the user may not open the specialist's screen */
  specialist?: AssistantCitation
  centre?: AssistantCitation
  telehealth?: boolean
  booked?: boolean
  reserved?: boolean
}

/** An email shown on a confirmation card. Body paragraphs may contain line breaks. */
export interface AssistantEmailPreview {
  to?: string
  subject?: string
  body?: string[]
}

export interface SendAssistantMessageBody {
  content: string
  /**
   * The record the user has open (kind + id, never a label): the server checks it like a tagged
   * record and names it itself. On a list screen: which list and, for a case list, its case type
   * - never what the list is filtered by.
   */
  pageContext?:
    | { recordType: string; recordId: string }
    | { listKind: string; serviceType?: string }
  /** The open case, for a server that does not read pageContext yet. */
  caseContextId?: string
  /** Slash command name (without "/") — forces the owning tool for this turn. */
  command?: string
  /** Records the user tagged via @-mentions in the composer. */
  mentions?: { entityType: string; id: string; label: string }[]
  clarificationResponse?: {
    messageId: string
    selectedValue?: string
    freeText?: string
  }
  /**
   * The same question again after a turn that failed or was cut off: the server does not store
   * it a second time while the conversation still ends with it unanswered.
   */
  retry?: boolean
}

/** No bytes for this long (the server heartbeats every 10s during tool calls) = dead stream. */
const STREAM_IDLE_TIMEOUT_MS = 120_000

export async function streamAssistantMessage(
  conversationId: string,
  body: SendAssistantMessageBody,
  handlers: AssistantStreamHandlers
) {
  // Every turn must end with exactly one done/error so the store never stays "streaming".
  let terminated = false
  const guarded: AssistantStreamHandlers = {
    ...handlers,
    onDone: payload => {
      terminated = true
      handlers.onDone(payload)
    },
    onError: (...args) => {
      terminated = true
      handlers.onError(...args)
    },
  }

  const post = (accessToken: string) =>
    fetch(
      `${process.env.VITE_API_URL}/api/app/assistant/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
        signal: handlers.signal,
      }
    )

  let idleTimer: ReturnType<typeof setTimeout> | undefined
  let idledOut = false

  try {
    let res = await post(GetAccessToken())

    // This fetch bypasses the axios 401 interceptor: refresh once and retry. A 401 is
    // returned before the turn runs, so re-sending cannot duplicate the message.
    if (res.status === 401) {
      let refreshed: string | undefined
      try {
        refreshed = await refreshAccessTokenShared()
      } catch {
        refreshed = undefined
      }
      if (!refreshed) {
        guarded.onError('SESSION_EXPIRED')
        return
      }
      res = await post(refreshed)
    }

    if (!res.ok || !res.body) {
      guarded.onError(errorCodeOfStatus(res.status))
      return
    }

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
    const armIdleTimer = () => {
      clearTimeout(idleTimer)
      idleTimer = setTimeout(() => {
        idledOut = true
        reader.cancel().catch(() => undefined)
      }, STREAM_IDLE_TIMEOUT_MS)
    }

    let buffer = ''
    armIdleTimer()
    for (;;) {
      const { value, done } = await reader.read()
      if (done) {
        break
      }
      armIdleTimer()
      buffer += value.replace(/\r\n/g, '\n')
      let idx = buffer.indexOf('\n\n')
      while (idx >= 0) {
        dispatchEvent(buffer.slice(0, idx), guarded)
        buffer = buffer.slice(idx + 2)
        idx = buffer.indexOf('\n\n')
      }
    }

    // A last frame without the trailing blank line is still a complete event.
    if (buffer.trim()) {
      dispatchEvent(buffer, guarded)
    }

    if (!terminated && !handlers.signal?.aborted) {
      guarded.onError(idledOut ? 'TIMEOUT' : 'CONNECTION_LOST')
    }
  } catch (err: any) {
    if (err?.name === 'AbortError' || handlers.signal?.aborted) {
      return
    }
    if (!terminated) {
      guarded.onError('CONNECTION_LOST')
    }
  } finally {
    clearTimeout(idleTimer)
  }
}

/**
 * A request the server answered before the stream opened. 400 and 413 are its limits on a
 * message (length, number of tagged records, body size): nothing of it was stored. 502, 503 and
 * 504 come from in front of the application (a restart, a deployment, a gateway that gave up):
 * the assistant is unavailable for a moment, and the same question can be sent again.
 */
function errorCodeOfStatus(status: number) {
  switch (status) {
    case 429:
      return 'RATE_LIMITED'
    case 400:
    case 413:
      return 'INVALID_INPUT'
    case 502:
    case 503:
    case 504:
      return 'SERVICE_UNAVAILABLE'
    default:
      return `HTTP_${status}`
  }
}

function dispatchEvent(rawFrame: string, handlers: AssistantStreamHandlers) {
  let eventName = ''
  const dataLines: string[] = []
  for (const line of rawFrame.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }
  if (!eventName || dataLines.length === 0) {
    return
  }

  let data: any
  try {
    data = JSON.parse(dataLines.join('\n'))
  } catch {
    return
  }

  switch (eventName) {
    case 'token':
      handlers.onEvent({ type: 'token', text: data.t ?? '' })
      break
    // tool_activity is the legacy twin of status: same label, sent once per tool call.
    case 'status':
    case 'tool_activity':
      handlers.onEvent({ type: 'status', label: data.label ?? 'Working…' })
      break
    case 'part':
      handlers.onEvent({ type: 'part', part: data })
      break
    case 'citations':
      handlers.onEvent({ type: 'citations', items: data.items ?? [] })
      break
    case 'clarification':
      handlers.onEvent({ type: 'clarification', clarification: data })
      break
    case 'done':
      handlers.onDone(data)
      break
    case 'title':
      handlers.onTitle?.(data)
      break
    case 'context':
      handlers.onContext?.({ ...data, used: data.used === true })
      break
    case 'error':
      handlers.onError(data.code, data.message)
      break
    default:
      // usage, legacy applied_filter, unknown future events
      break
  }
}
